import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { userDashboardLinks } from '../../routes/navConfig.jsx';
import { gamePageLinks } from '../../routes/gameScopeNav.jsx';
import { filterLinksByRole } from '../../utils/rbacUtils.js';
import { findGameBySlug, gameSlug } from '../../utils/gameSlug.js';

const MAX_RESULTS = 10;

function flatten(links, acc = [], crumbs = []) {
    for (const link of links) {
        const label = link.label;
        const path = link.path;
        const trail = [...crumbs, label];
        if (path) {
            acc.push({
                id: path,
                path,
                label,
                icon: link.icon,
                subtitle: trail.slice(0, -1).join(' / ') || null,
            });
        }
        if (link.subItems && link.subItems.length > 0) {
            flatten(link.subItems, acc, trail);
        }
    }
    return acc;
}

const AI_PATTERNS = /^(why|how|what|show me|tell me|explain|find|compare)\b/i;
const QUESTION_MARK = /\?/;

export function isAIQuery(query) {
    if (!query) return false;
    return AI_PATTERNS.test(query.trim()) || QUESTION_MARK.test(query);
}

/**
 * The pages the palette may offer, filtered exactly as the sidebar filters them (RBAC page / component gates,
 * super-user-only items): the hub nav, plus - when a game is in scope - that game's own pages (Live Events,
 * Segments, Configs, Settings ...), which are where most of the product lives. Nothing until permissions load:
 * suggesting first and refusing on click is the bug this replaces.
 *
 * @param game the game in scope (an accessible, non-DRAFT entry of userGames), or null
 */
export function buildPaletteIndex({ hubLinks = userDashboardLinks, userRole, effectivePermissions, game = null }) {
    if (!effectivePermissions) return [];
    const entries = flatten(filterLinksByRole(hubLinks, userRole, effectivePermissions));
    if (game && game.state !== 'DRAFT') {
        const name = game.gameName || game.id;
        const pages = flatten(filterLinksByRole(gamePageLinks(gameSlug(game)), userRole, effectivePermissions));
        for (const page of pages) {
            entries.push({ ...page, subtitle: page.subtitle ? `${name} / ${page.subtitle}` : name });
        }
    }
    const seen = new Set();
    return entries.filter(e => (seen.has(e.path) ? false : seen.add(e.path)));
}

/** @deprecated kept for callers of the hub-only index; prefer buildPaletteIndex. */
export function paletteIndex(links, userRole, effectivePermissions) {
    return buildPaletteIndex({ hubLinks: links, userRole, effectivePermissions });
}

/** The game the palette is about: the one in the URL, else the selected one (accessible games only). */
export function paletteGame(pathname, userGames, selectedGameId) {
    const games = Array.isArray(userGames) ? userGames : [];
    const slug = (pathname || '').match(/^\/game\/([^/]+)/)?.[1];
    if (slug && slug !== 'new') {
        const fromUrl = findGameBySlug(games, slug);
        if (fromUrl) return fromUrl;
    }
    return games.find(g => g?.id === selectedGameId) || null;
}

/** The permission-filtered palette index for the current user, org and game. */
export function useCommandPaletteIndex() {
    const userRole = useSelector(state => state.dashboardContext?.userRole);
    const effectivePermissions = useSelector(state => state.effectivePermissions);
    const userGames = useSelector(state => state.userGames);
    const selectedGameId = useSelector(state => state.dashboardContext?.selectedGameId);
    // The palette sits outside the router (it navigates via the router instance), so read the URL directly;
    // it is mounted fresh on every open, so this is the page it was opened over.
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const game = paletteGame(pathname, userGames, selectedGameId);
    return useMemo(
        () => buildPaletteIndex({ userRole, effectivePermissions, game }),
        [userRole, effectivePermissions, game],
    );
}

export function searchPaletteIndex(index, query) {
    const q = (query || '').trim().toLowerCase();
    if (q.length < 2) return [];
    const starts = [];
    const contains = [];
    for (const entry of index) {
        const label = entry.label.toLowerCase();
        const subtitle = (entry.subtitle || '').toLowerCase();
        if (label.startsWith(q)) starts.push(entry);
        else if (label.includes(q) || subtitle.includes(q)) contains.push(entry);
    }
    return [...starts, ...contains].slice(0, MAX_RESULTS);
}

export function useCommandPaletteSearch(query) {
    const index = useCommandPaletteIndex();
    return useMemo(() => searchPaletteIndex(index, query), [index, query]);
}
